import useMultiFetcherGenFilterReferenceData from "../services/middleware/hooks/useMultiFetcherGenFilterReference"
import {
    CATEGORY_OPTION_ID,
    DEVICE_TYPE_OPTION_ID,
    JURISDICTION_OPTION_ID,
    JURISDICTION_REF_KEY,
    MANUFACTURER_OPTION_ID,
    PHASE_OPTION_ID,
    TComplianceStatusRef,
    TDeviceCatRef,
    TDeviceTypes,
    TJurisdiction,
    TManufacturerRef,
    TPhasesRef,
} from "../services/middleware/mappers/genFilterReferenceMapper"

const createMappings = <
    T extends Record<string, string | number>,
    K extends number | string | symbol,
    V,
>(
    list: T[],
    keyCallback: (record: T) => K,
    valueCallback: (record: T) => V
): Record<K, V> => {
    const mapping: Partial<Record<K, V>> = {}

    list.forEach((item) => {
        const key = keyCallback(item)
        const value = valueCallback(item)
        mapping[key] = value
    })

    return mapping as Record<K, V>
}

const createManufacturerMappings = (
    manufacturerList: TManufacturerRef[]
): Record<number, string> =>
    createMappings(
        manufacturerList,
        (manufacturer) => manufacturer[MANUFACTURER_OPTION_ID],
        (manufacturer) => manufacturer.Manufacturer
    )

const createDeviceCategoryMappings = (
    categoryList: TDeviceCatRef[]
): Record<number, string> =>
    createMappings(
        categoryList,
        (category) => category[CATEGORY_OPTION_ID],
        (category) => category.DeviceCategory
    )

const createDeviceTypeMappings = (
    typeList: TDeviceTypes[]
): Record<number, string> =>
    createMappings(
        typeList,
        (type) => type[DEVICE_TYPE_OPTION_ID],
        (type) => type.DeviceType
    )

const createDevicePhaseMappings = (
    phaseList: TPhasesRef[]
): Record<number, string> =>
    createMappings(
        phaseList,
        (phase) => phase[PHASE_OPTION_ID],
        (phase) => phase.Phase
    )

const createJurisdictionMappings = (
    jurisdictionList: TJurisdiction[]
): Record<string, string> =>
    createMappings(
        jurisdictionList,
        (jurisdiction) => jurisdiction[JURISDICTION_OPTION_ID],
        (jurisdiction) => jurisdiction[JURISDICTION_REF_KEY]
    )

const createComplianceMapping = (
    complianceList: TComplianceStatusRef[]
): Record<number, string> =>
    createMappings(
        complianceList,
        (compliance) => compliance.ComplianceStatusID,
        (compliance) => compliance.StatusShort
    )

export const useFilterReferenceMappings = (): {
    deviceCategoryMappings: Record<number, string>
    deviceTypeMappings: Record<number, string>
    devicePhaseMappings: Record<number, string>
    manufacturerMappings: Record<number, string>
    jurisdictionMappings: Record<string, string>
    complianceMappings: Record<number, string>
    complianceStatusIds: number[]
    deviceCategoryIds: number[]
    deviceTypeIds: number[]
    devicePhaseIds: number[]
    manufacturerIds: number[]
    isLoading: boolean
    isError: boolean
} => {
    const { referenceData, isLoading, isError } =
        useMultiFetcherGenFilterReferenceData()

    const loaded = !isLoading && !isError

    const deviceCategoryList = referenceData?.deviceCategories ?? []
    const deviceTypeList = referenceData?.deviceTypes ?? []
    const devicePhaseList = referenceData?.phase_id ?? []
    const manufacturerList = referenceData?.deviceManufacturers ?? []
    const jurisdictionList = referenceData?.jurisdiction ?? []
    const complianceList = referenceData?.complianceStatuses ?? []

    const deviceCategoryMappings = loaded
        ? createDeviceCategoryMappings(deviceCategoryList)
        : {}

    const deviceTypeMappings = loaded
        ? createDeviceTypeMappings(deviceTypeList)
        : {}

    const devicePhaseMappings = loaded
        ? createDevicePhaseMappings(devicePhaseList)
        : {}

    const manufacturerMappings = loaded
        ? createManufacturerMappings(manufacturerList)
        : {}

    const jurisdictionMappings = loaded
        ? createJurisdictionMappings(jurisdictionList)
        : {}

    const complianceMappings = loaded
        ? createComplianceMapping(complianceList)
        : {}

    return {
        deviceCategoryMappings,
        deviceTypeMappings,
        devicePhaseMappings,
        manufacturerMappings,
        jurisdictionMappings,
        complianceMappings,
        complianceStatusIds: complianceList.map(
            (compliance) => compliance.ComplianceStatusID
        ),
        deviceCategoryIds: deviceCategoryList.map(
            (category) => category[CATEGORY_OPTION_ID]
        ),
        deviceTypeIds: deviceTypeList.map(
            (type) => type[DEVICE_TYPE_OPTION_ID]
        ),
        devicePhaseIds: devicePhaseList.map((phase) => phase[PHASE_OPTION_ID]),
        manufacturerIds: manufacturerList.map(
            (manufacturer) => manufacturer[MANUFACTURER_OPTION_ID]
        ),
        isLoading,
        isError,
    }
}
